import {Box, Hero} from '@primer/react-brand'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ContentfulAppStoreButtonGroup} from '../ContentfulAppStoreButtonGroup/ContentfulAppStoreButtonGroup'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {getImageSources, MAX_CONTENT_WIDTH} from '../../../lib/utils/images'
import styles from './ContentfulHero.module.css'
import {ContentfulAnimatedVideo} from '../ContentfulAnimatedVideo/ContentfulAnimatedVideo'
import {IframeVideo} from '../../IframeVideo/IframeVideo'
import type {ContentfulHeroProps} from './contentful-hero.types'
import {renderSingleLineRichText} from '../shared/RichTextFieldRenderer'
import {useNinetailedTrack} from '../../ninetailed/NinetailedTrackContext'

/**
 * Hero layout component used by both baseline and Ninetailed experiences.
 * Handles all the visual rendering logic for ContentfulHero.
 */
export function ContentfulHeroLayout({
  component,
  paddingBlockEnd,
  analyticsContext,
  trailingComponentOverride,
  ...props
}: ContentfulHeroProps) {
  const enableCodeBlocks = isFeatureEnabled('contentful_primer_code_blocks')
  const track = useNinetailedTrack()
  const {
    align,
    callToActionPrimary,
    callToActionSecondary,
    description,
    descriptionRich,
    descriptionVariant,
    heading,
    headingSize,
    image,
    imagePosition,
    label,
    increaseLabelContrast,
    trailingComponent,
    videoSrc,
    animatedVideo,
  } = component.fields

  const isHeroImage = image && !videoSrc

  const resolvedTrailingComponent =
    trailingComponentOverride ??
    (trailingComponent !== undefined && trailingComponent.length > 0 ? (
      <ContentfulAppStoreButtonGroup components={trailingComponent} analyticsLabel="hero" analyticsLocation="hero" />
    ) : undefined)

  const hasTrailingComponent = resolvedTrailingComponent !== undefined

  const {heroClassName = 'pb-0'} = props

  return (
    <Box className={styles.contentfulHeroContainer} paddingBlockEnd={paddingBlockEnd || {narrow: 96, regular: 128}}>
      <Hero
        data-hpc={props['data-hpc']}
        align={align}
        trailingComponent={hasTrailingComponent ? () => resolvedTrailingComponent : undefined}
        className={heroClassName}
      >
        {label &&
          (typeof label === 'string' ? (
            <Hero.Label {...(increaseLabelContrast ? {variant: 'default'} : {})}>{label}</Hero.Label>
          ) : (
            <Hero.Label {...(increaseLabelContrast ? {variant: 'default'} : {})}>{label.fields.text}</Hero.Label>
          ))}

        <Hero.Heading size={headingSize}>{heading}</Hero.Heading>

        {descriptionRich ? (
          <Hero.Description variant={descriptionVariant}>
            {renderSingleLineRichText(descriptionRich, enableCodeBlocks, 'description')}
          </Hero.Description>
        ) : description ? (
          <Hero.Description variant={descriptionVariant}>{description}</Hero.Description>
        ) : null}

        {!hasTrailingComponent && callToActionPrimary ? (
          <Hero.PrimaryAction
            href={callToActionPrimary.fields.href}
            data-ref={`hero-primary-action-${callToActionPrimary.sys.id}`}
            data-vendor-pixel-event=""
            {...getAnalyticsEvent(
              {
                action: callToActionPrimary.fields.text,
                tag: 'button',
                location: 'hero',
                context: analyticsContext ? `strategy:${analyticsContext}` : 'CTAs',
              },
              {context: false},
            )}
            {...({
              onClick: () => track('primary_cta_click', {label: callToActionPrimary.fields.text, location: 'hero'}),
            } as Record<string, unknown>)}
          >
            {callToActionPrimary.fields.text}
          </Hero.PrimaryAction>
        ) : null}

        {!hasTrailingComponent && callToActionSecondary ? (
          <Hero.SecondaryAction
            href={callToActionSecondary.fields.href}
            {...getAnalyticsEvent(
              {
                action: callToActionSecondary.fields.text,
                tag: 'button',
                location: 'hero',
                context: analyticsContext ? `strategy:${analyticsContext}` : 'CTAs',
              },
              {context: false},
            )}
            {...({
              onClick: () => track('secondary_cta_click', {label: callToActionSecondary.fields.text, location: 'hero'}),
            } as Record<string, unknown>)}
          >
            {callToActionSecondary.fields.text}
          </Hero.SecondaryAction>
        ) : null}
        {isHeroImage ? (
          <Hero.Image
            as="picture"
            position={imagePosition === 'Inline' ? 'inline-end' : 'block-end'}
            src={`${image.fields.file.url}?fm=webp`}
            sources={getImageSources(image.fields.file.url, {maxWidth: MAX_CONTENT_WIDTH})}
            alt={image.fields.description || ''}
          />
        ) : null}
      </Hero>

      {videoSrc !== undefined && !isHeroImage ? (
        <IframeVideo videoSrc={videoSrc} heading={heading} image={image} className={styles.heroVideo} />
      ) : null}
      {!videoSrc && !image && animatedVideo && (
        <Box paddingBlockStart={80}>
          <ContentfulAnimatedVideo component={animatedVideo} analyticsLocation="hero" />
        </Box>
      )}
    </Box>
  )
}
